if (!customElements.get('cro-os-popup')) {
  customElements.define(
    'cro-os-popup',
    class CroOsPopup extends HTMLElement {
      abortController = undefined;

      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('.component-cro-os-popup');
        this.form = this.querySelector('.component-cro-os-popup__form');
        this.emailInput = this.querySelector('[data-cro-os-popup-email]');
        this.submitButton = this.querySelector('[data-cro-os-popup-submit]');
        this.message = this.querySelector('[data-cro-os-popup-message]');
        this.closeButton = this.querySelector('[data-cro-os-popup-close]');

        if (this.hasSubscribedAccess()) {
          this.remove();
          return;
        }

        if (this.hasNewsletterBypass()) {
          this.remove();
          return;
        }

        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.onCloseClick);
        }

        if (this.form) {
          this.form.setAttribute('novalidate', 'novalidate');
          this.form.addEventListener('submit', this.onSubmit);
        }

        this.open();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.closeButton?.removeEventListener('click', this.onCloseClick);

        if (this.form) {
          this.form.removeEventListener('submit', this.onSubmit);
        }
      }

      open() {
        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');
      }

      close() {
        this.classList.remove('is-visible');
        this.setAttribute('aria-hidden', 'true');
      }

      onCloseClick() {
        this.close();
      }

      async onSubmit(event) {
        event.preventDefault();

        const email = this.normalizedEmail;

        if (!this.form?.reportValidity()) {
          return;
        }

        this.subscribeShopifyCustomer();
        await this.subscribeEmail(email);
      }

      async subscribeEmail(email) {
        this.setLoading(true);
        this.showMessage('', 'hidden');

        try {
          const didSubscribe = await this.requestAccess(email);

          if (!didSubscribe) {
            this.showMessage('Something went wrong. Please try again.', 'error');
            return;
          }

          this.persistAccess({
            email,
            subscribed: true,
            verifiedAt: new Date().toISOString(),
          });

          const thankYouUrl = this.dataset.thankYouUrl;

          if (thankYouUrl) {
            // window.location.assign(thankYouUrl);
            this.remove();
            return;
          }

          this.remove();
        } catch (error) {
          this.showMessage('Something went wrong. Please try again.', 'error');
        } finally {
          this.setLoading(false);
        }
      }

      subscribeShopifyCustomer() {
        if (!this.form) {
          return;
        }

        this.submitShopifyFormAjax(this.form);
      }

      submitShopifyFormAjax(shopifyForm) {
        const body = this.buildShopifyFormBody(shopifyForm);
        const action = shopifyForm.getAttribute('action');

        if (!body || !action) {
          return;
        }

        try {
          fetch(action, {
            method: 'POST',
            body,
            credentials: 'same-origin',
            keepalive: true,
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              Accept: 'text/html, */*; q=0.01',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
        } catch (error) {
          /* Shopify subscribe should not block Beehiiv webhook delivery. */
        }
      }

      buildShopifyFormBody(shopifyForm) {
        const params = new URLSearchParams();
        const inputs = shopifyForm.querySelectorAll('[name]');

        for (const input of inputs) {
          if (input.disabled || !input.name) {
            continue;
          }

          if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
            continue;
          }

          params.append(input.name, input.value);
        }

        return params.toString();
      }

      async requestAccess(email) {
        const apiUrl = this.dataset.apiUrl;

        if (!apiUrl) {
          return true;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();

        const response = await fetch(apiUrl, {
          method: 'POST',
          signal: this.abortController.signal,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            tags: this.resolveApiTags(),
          }),
        });

        return response.ok;
      }

      resolveApiTags() {
        const baseTags = (this.dataset.apiTags || 'beehiiv, creator, source-cro-popup')
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);

        if (this.matchesUtmForCody()) {
          baseTags.push('source-cody');
        }

        return baseTags.join(', ');
      }

      matchesUtmForCody() {
        const params = new URLSearchParams(window.location.search);
        const values = [
          params.get('utm_source'),
          params.get('utm_campaign'),
          params.get('utm_medium'),
        ];

        for (const value of values) {
          if (value && value.toLowerCase().includes('cody')) {
            return true;
          }
        }

        return false;
      }

      hasNewsletterBypass() {
        const storageKey = this.dataset.newsletterBypassKey || 'croOsPopupNewsletterBypass';

        try {
          return sessionStorage.getItem(storageKey) === '1';
        } catch (error) {
          return document.documentElement.classList.contains('cro-os-popup-suppressed');
        }
      }

      hasSubscribedAccess() {
        const access = this.readCookieAccess();
        return access?.subscribed === true && Boolean(access?.email);
      }

      readCookieAccess() {
        const cookiePrefix = `${this.encodedCookieName}=`;

        try {
          const cookies = document.cookie.split('; ');

          for (const cookie of cookies) {
            if (!cookie.startsWith(cookiePrefix)) {
              continue;
            }

            return JSON.parse(decodeURIComponent(cookie.slice(cookiePrefix.length)));
          }
        } catch (error) {
          return null;
        }

        return null;
      }

      persistAccess(accessPayload) {
        const serializedAccess = JSON.stringify(accessPayload);

        try {
          const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';

          document.cookie = `${this.encodedCookieName}=${encodeURIComponent(serializedAccess)}; Max-Age=31536000; Path=/; SameSite=Lax${secureFlag}`;
        } catch (error) {
          /* Cookies unavailable. */
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) {
          return;
        }

        this.submitButton.disabled = isLoading;
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      showMessage(text, type) {
        if (!this.message) {
          return;
        }

        if (type === 'hidden' || !text) {
          this.message.hidden = true;
          this.message.textContent = '';
          this.message.className = 'component-cro-os-popup__message';
          return;
        }

        this.message.hidden = false;
        this.message.textContent = text;
        this.message.className = `component-cro-os-popup__message component-cro-os-popup__message--${type}`;
      }

      get normalizedEmail() {
        return (this.emailInput?.value || '').trim().toLowerCase();
      }

      get cookieName() {
        return this.dataset.cookieName || 'croOsPopupAccess';
      }

      get encodedCookieName() {
        return encodeURIComponent(this.cookieName);
      }
    }
  );
}
